// Détail d'une facture proforma : émission, réponse du client, conversion en facture.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { AttenteValidation, DemanderValidation, ValiderEmettre, ValideePar } from '../components/ValidationEmission';
import { proformaAPI } from '../services/api';
import { apiError, dateFr, dateHeureFr, fcfa, PROFORMA_STATUS } from '../utils/format';

const Proforma = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canCompta } = useAuth();
  const [pf, setPf] = useState(null);
  const [error, setError] = useState('');
  const location = useLocation();
  const [message, setMessage] = useState(location.state?.info || '');
  const [refus, setRefus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { proformaAPI.get(id).then((r) => setPf(r.data)).catch((e) => setError(apiError(e))); }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, after) => {
    setBusy(true); setError(''); setMessage('');
    try { const r = await fn(); setMessage(r.data.message); if (after) after(r); else load(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  if (!pf) return <Layout title="Proforma">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;
  const st = PROFORMA_STATUS[pf.statut];
  const ouverte = ['EMISE', 'ACCEPTEE'].includes(pf.statut);

  return (
    <Layout title={pf.numero ? `Proforma ${pf.numero}` : 'Proforma (brouillon)'}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={st.label} color={st.color} />
        {pf.expiree && <Chip label="Validité dépassée" color="warning" variant="outlined" />}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={() => window.open(`/proformas/${id}/imprimer`, '_blank')}>{pf.statut === 'BROUILLON' ? 'Aperçu' : 'Imprimer / PDF'}</Button>
        {pf.statut === 'BROUILLON' && (
          <>
            {canCompta && <Button variant="outlined" onClick={() => navigate(`/proformas/${id}/modifier`)}>Modifier</Button>}
            {canCompta && <Button color="error" onClick={() => window.confirm('Supprimer ce brouillon ?') && run(() => proformaAPI.remove(id), () => navigate('/proformas'))}>Supprimer</Button>}
            <DemanderValidation kind="proformas" id={id} doc={pf} onDone={(m, sev) => { if (sev === 'error') setError(m); else { setMessage(m); load(); } }} />
            <ValiderEmettre label="la proforma" busy={busy} onEmit={() => run(() => proformaAPI.emit(id))} />
          </>
        )}
        {pf.statut === 'EMISE' && canCompta && (
          <>
            <Button color="error" onClick={() => setRefus('')}>Refusée par le client</Button>
            <Button variant="outlined" color="success" disabled={busy} onClick={() => run(() => proformaAPI.respond(id, 'ACCEPTEE'))}>Acceptée par le client</Button>
          </>
        )}
        {ouverte && canCompta && (
          <Button variant="contained" color="success" disabled={busy}
            onClick={() => window.confirm('Créer la facture à partir de cette proforma ? (brouillon de facture, à vérifier puis émettre)')
              && run(() => proformaAPI.convert(id), (r) => navigate(`/factures/${r.data.invoice_id}`))}>
            Facturer
          </Button>
        )}
        {pf.statut !== 'BROUILLON' && canCompta && (
          <Button disabled={busy} onClick={() => run(() => proformaAPI.duplicate(id), (r) => navigate(`/proformas/${r.data.id}/modifier`))}>Dupliquer</Button>
        )}
      </Box>

      {pf.statut === 'BROUILLON' && <AttenteValidation kind="proformas" doc={pf} />}
      {pf.statut !== 'BROUILLON' && <ValideePar doc={pf} />}
      {pf.statut === 'REFUSEE' && <Alert severity="warning" sx={{ mb: 2 }}>Refusée — {pf.motif_refus}</Alert>}
      {pf.invoice_id && (
        <Alert severity="success" sx={{ mb: 2 }} action={<Button onClick={() => navigate(`/factures/${pf.invoice_id}`)}>Ouvrir la facture</Button>}>
          Facturée : {pf.invoice_numero || 'facture en brouillon'}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline">Client</Typography>
            <Typography variant="h6">{pf.client_nom}</Typography>
            <Typography>{pf.client_adresse}</Typography>
            <Typography>{pf.client_ville}</Typography>
            {pf.client_ninea && <Typography variant="body2">NINEA : {pf.client_ninea}</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline">Proforma</Typography>
            <Typography>Date : <strong>{dateFr(pf.date_proforma)}</strong> — Valable jusqu'au : <strong>{dateFr(pf.date_validite)}</strong></Typography>
            {pf.objet && <Typography>Objet : {pf.objet}</Typography>}
            {pf.conditions && <Typography>Conditions : {pf.conditions}</Typography>}
            {pf.emise_le && <Typography variant="body2" color="text.secondary">Émise le {dateHeureFr(pf.emise_le)} par {pf.emise_par_username}</Typography>}
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell><TableCell>Désignation</TableCell><TableCell align="right">Qté</TableCell>
              <TableCell align="right">Prix HT</TableCell><TableCell align="right">TVA</TableCell><TableCell align="right">Total TTC</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pf.lines.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.code}</TableCell><TableCell>{l.designation}</TableCell><TableCell align="right">{l.quantite}</TableCell>
                <TableCell align="right">{fcfa(l.prix_unitaire_ht)}</TableCell><TableCell align="right">{l.taux_tva}%</TableCell><TableCell align="right">{fcfa(l.total_ttc)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, flexWrap: 'wrap' }}>
        {pf.montant_remise > 0 && (
          <>
            <Typography>Montant brut HT <strong>{fcfa(pf.total_brut_ht)}</strong></Typography>
            <Typography color="error.main">Remise {String(pf.remise_pct).replace('.', ',')} % <strong>−{fcfa(pf.montant_remise)}</strong></Typography>
          </>
        )}
        <Typography>Total HT <strong>{fcfa(pf.total_ht)}</strong></Typography>
        <Typography>TVA <strong>{fcfa(pf.total_tva)}</strong></Typography>
        <Typography variant="h6">Total TTC {fcfa(pf.total_ttc)} FCFA</Typography>
      </Box>

      <Dialog open={refus !== null} onClose={() => setRefus(null)} fullWidth maxWidth="xs">
        <DialogTitle>Proforma refusée par le client</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField fullWidth autoFocus multiline minRows={2} label="Motif (prix, délai, concurrent…)" value={refus || ''} onChange={(e) => setRefus(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefus(null)}>Retour</Button>
          <Button variant="contained" color="error" disabled={!refus?.trim()} onClick={() => run(() => proformaAPI.respond(id, 'REFUSEE', refus), () => { setRefus(null); load(); })}>Enregistrer le refus</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Proforma;
